import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaSpinner, 
  FaUser, 
  FaEnvelope, 
  FaPhone,
  FaMapMarkerAlt,
  FaBriefcase,
  FaFileAlt,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaStar
} from 'react-icons/fa';
import api from '../../services/api';
import './CareerDetail.css';

function CareerDetail() {
  const { id } = useParams();
  const [career, setCareer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCareer();
  }, [id]);

  const fetchCareer = async () => {
    try {
      const response = await api.get(`/api/admin/careers/${id}`);
      setCareer(response.data.career || response.data);
    } catch (error) {
      console.error('Error fetching career:', error);
      setError('Failed to load career application details');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await api.patch(`/api/admin/careers/${id}`, { status: newStatus });
      setCareer(response.data.career || response.data);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      new: 'status-new',
      reviewing: 'status-reviewing',
      shortlisted: 'status-shortlisted',
      rejected: 'status-rejected',
      hired: 'status-hired'
    };
    return statusColors[status] || 'status-new';
  };

  if (loading) {
    return (
      <div className="career-detail-loading">
        <FaSpinner className="spinner" />
        <p>Loading application details...</p>
      </div>
    );
  }

  if (error || !career) {
    return (
      <div className="career-detail-error">
        <p>{error || 'Application not found'}</p>
        <Link to="/admin/dashboard/careers" className="back-btn">
          <FaArrowLeft /> Back to Applications
        </Link>
      </div>
    );
  }

  return (
    <div className="career-detail-page">
      <div className="career-detail-header">
        <Link to="/admin/dashboard/careers" className="back-link">
          <FaArrowLeft /> Back to Applications
        </Link>
        <h2>Career Application Details</h2>
        <span className="career-date">
          <FaClock /> Applied: {formatDate(career.createdAt)}
        </span>
      </div>

      <div className="career-detail-content">
        {/* Applicant Information Card */}
        <div className="career-info-card">
          <div className="career-avatar-large">
            {career.fullName.charAt(0).toUpperCase()}
          </div>
          <h3>{career.fullName}</h3>
          <div className={`status-badge-large ${getStatusBadge(career.status)}`}>
            {career.status.charAt(0).toUpperCase() + career.status.slice(1)}
          </div>
          <div className="career-info-items">
            <a href={`mailto:${career.email}`} className="career-info-item">
              <FaEnvelope />
              <span>{career.email}</span>
            </a>
            <a href={`tel:${career.phone}`} className="career-info-item">
              <FaPhone />
              <span>{career.phone}</span>
            </a>
            <div className="career-info-item">
              <FaMapMarkerAlt />
              <span>{career.address}</span>
            </div>
          </div>
        </div>

        {/* Application Details Card */}
        <div className="application-card">
          <div className="application-section">
            <h3>
              <FaBriefcase /> Position Applied For
            </h3>
            <p>{career.position || 'Not specified'}</p>
          </div>

          <div className="application-section">
            <h3>Years of Experience</h3>
            <p>{career.experience || 'Not specified'}</p>
          </div>

          {career.coverLetter && (
            <div className="application-section">
              <h3>
                <FaFileAlt /> Cover Letter
              </h3>
              <div className="cover-letter-text">
                {career.coverLetter}
              </div>
            </div>
          )}

          {career.cvFileName && (
            <div className="application-section">
              <h3>CV/Resume</h3>
              <p className="cv-file-name">
                <FaFileAlt /> {career.cvFileName}
              </p>
            </div>
          )}

          {/* Status Actions */}
          <div className="status-actions">
            <h3>Update Application Status</h3>
            <div className="status-buttons">
              <button 
                onClick={() => updateStatus('reviewing')}
                disabled={updating || career.status === 'reviewing'}
                className={`status-btn ${career.status === 'reviewing' ? 'active' : ''}`}
              >
                <FaClock /> Reviewing
              </button>
              <button 
                onClick={() => updateStatus('shortlisted')}
                disabled={updating || career.status === 'shortlisted'}
                className={`status-btn ${career.status === 'shortlisted' ? 'active' : ''}`}
              >
                <FaStar /> Shortlisted
              </button>
              <button 
                onClick={() => updateStatus('hired')}
                disabled={updating || career.status === 'hired'}
                className={`status-btn success ${career.status === 'hired' ? 'active' : ''}`}
              >
                <FaCheckCircle /> Hired
              </button>
              <button 
                onClick={() => updateStatus('rejected')}
                disabled={updating || career.status === 'rejected'}
                className={`status-btn danger ${career.status === 'rejected' ? 'active' : ''}`}
              >
                <FaTimesCircle /> Rejected
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <a 
              href={`mailto:${career.email}?subject=Re: Your Application - ${career.position || 'Position'}`}
              className="action-btn primary"
            >
              <FaEnvelope /> Email Applicant
            </a>
            <a 
              href={`tel:${career.phone}`}
              className="action-btn secondary"
            >
              <FaPhone /> Call Applicant
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CareerDetail;


